// Persistence helpers for the auth shader backdrop. Kept in their own module
// so the component file only exports a component (Fast Refresh constraint) and
// the pure parse/clamp logic stays unit-testable in isolation.

// Persisted animation clock (seconds). Keeping the smoke at the same place
// across reloads avoids the jarring "reset to frame 0" on every navigation.
export const TIME_STORAGE_KEY = 'reflex.auth-shader.time';

// Persisted smoothed cursor influence (0..1, y up). The `u_mouse` uniform
// drives the whole composition — parallax, domain warp, and directional
// light — so restoring it alongside the clock means a refresh reproduces the
// exact same frame instead of snapping the clouds back to the default origin.
export const MOUSE_STORAGE_KEY = 'reflex.auth-shader.mouse';

// Cap restored values so a very stale clock can't push the smoke into an
// extreme region of the noise field (and guard against garbage in storage).
const MAX_PERSISTED_TIME = 100000;

// Default cursor influence used before any pointer movement (and as a fallback
// when nothing valid is persisted). Centered vertically so the halo sits behind
// the sign-in card by default — important on touch devices, where there is no
// pointer to move it off the top.
export const DEFAULT_MOUSE = { x: 0.5, y: 0.5 } as const;

export function readPersistedTime(): number {
  try {
    const raw = window.localStorage.getItem(TIME_STORAGE_KEY);
    if (!raw) return 0;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return parsed % MAX_PERSISTED_TIME;
  } catch {
    return 0;
  }
}

export function readPersistedMouse(): { x: number; y: number } {
  try {
    const raw = window.localStorage.getItem(MOUSE_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_MOUSE };
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'x' in parsed &&
      'y' in parsed &&
      typeof (parsed as { x: unknown }).x === 'number' &&
      typeof (parsed as { y: unknown }).y === 'number'
    ) {
      const { x, y } = parsed as { x: number; y: number };
      if (Number.isFinite(x) && Number.isFinite(y)) {
        // Clamp to the 0..1 range the shader expects.
        return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
      }
    }
    return { ...DEFAULT_MOUSE };
  } catch {
    return { ...DEFAULT_MOUSE };
  }
}

// Persist both the clock and the current cursor influence together so a reload
// reproduces the exact same shader frame (same `u_time` and the same
// `u_mouse`), not just the same animation phase.
export function persistShaderState(time: number, mouse: { x: number; y: number }): void {
  try {
    window.localStorage.setItem(TIME_STORAGE_KEY, time.toFixed(2));
    window.localStorage.setItem(
      MOUSE_STORAGE_KEY,
      JSON.stringify({ x: Number(mouse.x.toFixed(4)), y: Number(mouse.y.toFixed(4)) }),
    );
  } catch {
    // Ignore storage failures (private mode, quota) — persistence is a nicety,
    // the effect still runs from the in-memory clock.
  }
}
